
import React, { useState, useEffect } from 'react';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Skeleton } from '@/components/ui/skeleton';

const ImageCollage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [collageLoaded, setCollageLoaded] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 2;

  // Preload the collage image with improved error handling and faster timeout
  useEffect(() => {
    // Immediately start loading both primary image and fallbacks
    const loadMainImage = () => {
      const preloadImage = new Image();
      preloadImage.src = '/lovable-uploads/collage-image-combined.png';
      
      // Use these attributes for faster loading
      preloadImage.decoding = 'async';
      preloadImage.fetchPriority = 'high';
      
      preloadImage.onload = () => {
        setCollageLoaded(true);
        setIsLoading(false);
      };
      
      preloadImage.onerror = () => {
        // If we haven't exceeded max retries, try again
        if (retryCount < maxRetries) {
          setRetryCount(prev => prev + 1);
          loadMainImage(); // Retry loading
        } else {
          setIsLoading(false); // Stop loading state but keep collageLoaded as false to show fallback
        }
      };
    };
    
    loadMainImage();
    
    // Pre-load the fallback grid images in parallel
    const fallbackImages = [
      "/lovable-uploads/085d6c6c-f487-443d-b214-d88c6b2f56a3.png",
      "/lovable-uploads/f042036e-4444-4e68-9c2b-c762c22f6edb.png",
      "/lovable-uploads/1b5aadfd-c57e-465e-baea-d76bce605534.png",
      "/lovable-uploads/cae79bb5-2496-4448-9f79-9282f7ae068c.png",
      "/lovable-uploads/1b89a2b3-aabb-4b73-862c-b0c19e6db642.png",
      "/lovable-uploads/8e4b4cc5-c4c0-40c0-8770-7e87c5c67cd9.png",
      "/lovable-uploads/014b3605-0981-4583-b507-1b4198efeeb2.png",
      "/lovable-uploads/66ee1349-161a-4483-bab6-9ad31997651e.png",
      "/lovable-uploads/b1776489-2909-4359-8225-8e21421e5fe0.png"
    ];
    
    fallbackImages.forEach(src => {
      const img = new Image();
      img.src = src;
      img.fetchPriority = 'high';
    });
    
    // Shorter timeout (800ms) to switch to fallback quicker if needed
    const timer = setTimeout(() => {
      if (!collageLoaded) {
        setIsLoading(false);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [retryCount, collageLoaded]);

  return (
    <div className="overflow-hidden shadow-md shadow-purple-100/70 transform transition-transform duration-500 hover:scale-[1.01] parallax-element rounded-xl" data-speed="0.04">
      <AspectRatio ratio={1} className="w-full max-w-[90%] mx-auto">
        <div className="bg-white/90 p-3 h-full rounded-lg">
          {isLoading ? (
            <Skeleton className="w-full h-full rounded-lg animate-pulse" />
          ) : (
            <>
              {collageLoaded && (
                <img 
                  src="/lovable-uploads/collage-image-combined.png" 
                  alt="Collage of exam students" 
                  className="w-full h-full object-cover rounded-lg"
                  width="900"
                  height="900"
                  fetchPriority="high"
                  loading="eager"
                  decoding="async"
                  onError={() => setCollageLoaded(false)}
                />
              )}
              
              {/* Fallback grid layout - only shown if collage doesn't load */}
              {!collageLoaded && (
                <div 
                  className="grid grid-cols-3 grid-rows-3 gap-2 h-full"
                >
                  {[
                    {src: "/lovable-uploads/085d6c6c-f487-443d-b214-d88c6b2f56a3.png", alt: "Happy student with exam papers"},
                    {src: "/lovable-uploads/f042036e-4444-4e68-9c2b-c762c22f6edb.png", alt: "Students celebrating exam results"},
                    {src: "/lovable-uploads/1b5aadfd-c57e-465e-baea-d76bce605534.png", alt: "Student celebrating with exam paper"},
                    {src: "/lovable-uploads/cae79bb5-2496-4448-9f79-9282f7ae068c.png", alt: "Students taking exam with invigilator"},
                    {src: "/lovable-uploads/1b89a2b3-aabb-4b73-862c-b0c19e6db642.png", alt: "Overhead view of exam hall"},
                    {src: "/lovable-uploads/8e4b4cc5-c4c0-40c0-8770-7e87c5c67cd9.png", alt: "Students focusing on exam papers"},
                    {src: "/lovable-uploads/014b3605-0981-4583-b507-1b4198efeeb2.png", alt: "Student excited over exam results"},
                    {src: "/lovable-uploads/66ee1349-161a-4483-bab6-9ad31997651e.png", alt: "Group of students studying together"},
                    {src: "/lovable-uploads/b1776489-2909-4359-8225-8e21421e5fe0.png", alt: "Students celebrating with hug"}
                  ].map((image, index) => (
                    <img 
                      key={index}
                      src={image.src} 
                      alt={image.alt}
                      className="w-full h-full object-cover rounded-lg"
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </AspectRatio>
    </div>
  );
};

export default ImageCollage;
